
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import {
  Button,
  Card,
  CircularLoader,
  AlertBar,
  Input,
  TextArea,
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  IconSearch24,
  SingleSelect,
  SingleSelectOption,
  Chip,
  colors
} from '@dhis2/ui';
import { 
  UserIcon, 
  ChevronDownIcon, 
  ChevronRightIcon,
  ClockIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  MessageCircleIcon,
  ChevronLeft,
  ChevronRight,
  Users,
  FileIcon,
  UsersRoundIcon
} from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { usePerformanceContext } from '../../performance/PerformanceContext';
import { useNavigate } from 'react-router-dom';
import { FileAttachment } from './FileAttachment';
import { GiCheckboxTree, GiCheckMark, GiNotebook } from "react-icons/gi";
import { resolveActiveFiscalYear } from '../../GoalsManagement/utils/fiscalYearHelpers';

// Import POSITION_ATTRIBUTES from constants
import { POSITION_ATTRIBUTES } from '../../staff/api/constants';

const PROGRAM_ID = 'yiT00XKrrHP';
const TASK_PROGRAM_STAGE_ID = 'Nv9nYNcBTdM';

// Helper function to check if a date is within fiscal year
const isWithinFiscalYear = (dateStr, fiscalYear) => {
  if (!dateStr || !fiscalYear?.start || !fiscalYear?.end) return false;
  const date = new Date(dateStr);
  const start = new Date(fiscalYear.start);
  const end = new Date(fiscalYear.end);
  return date >= start && date <= end;
};

  const getFiscalYear = () => {
    try {
      const storedCalendar = localStorage.getItem('company_financial_calendar');
      if (storedCalendar) {
        const parsedCalendar = JSON.parse(storedCalendar);
        if (parsedCalendar?.financialYearStart && parsedCalendar?.financialYearEnd) {
          return {
            start: parsedCalendar.financialYearStart,
            end: parsedCalendar.financialYearEnd,
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load fiscal year from localStorage:', error);
    }
    return null; // No fiscal year configured
  };

// Global styles for animations
const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

// ============================================================================
// DESIGN TOKENS
// ============================================================================
const TOKENS = {
  bgPrimary: '#f4f6fa',
  bgCard: '#ffffff',
  bgCardAlt: '#f0f4ff',
  bgSurface: '#eef2f7',
  borderSubtle: '#e2e8f0',
  borderStrong: '#cbd5e1',
  textPrimary: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#94a3b8',
  accent: '#3b82f6',
  accentSoft: '#dbeafe',
  accentText: '#1d4ed8',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  shadowSm: '0 1px 2px 0 rgba(15,23,42,0.06)',
  shadowMd: '0 4px 6px -1px rgba(15,23,42,0.07), 0 2px 4px -2px rgba(15,23,42,0.05)',
  shadowLg: '0 10px 25px -5px rgba(15,23,42,0.10), 0 8px 10px -6px rgba(15,23,42,0.06)',
  shadowFocus: '0 0 0 3px rgba(59,130,246,0.25)',
  radiusXs: '6px',
  radiusSm: '8px',
  radiusMd: '12px',
  radiusLg: '16px',
  radiusFull: '9999px',
};

const cx = (...args) => args.filter(Boolean).join(' ');

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const StatusBadge = ({ progress }) => {
  const getStatusInfo = (p) => {
    if (p === 100) return { color: TOKENS.success, label: 'Complete', icon: <CheckCircleIcon size={13} /> };
    if (p > 0 && p < 100) return { color: TOKENS.warning, label: 'In Progress', icon: <ClockIcon size={13} /> };
    return { color: TOKENS.warning, label: 'In Progress', icon: <ClockIcon size={13} /> };
  };

  const info = getStatusInfo(progress);
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '3px 10px',
      borderRadius: TOKENS.radiusFull,
      backgroundColor: `${info.color}14`,
      color: info.color,
      fontSize: '0.7rem',
      fontWeight: 600,
      letterSpacing: '0.01em',
      border: `1px solid ${info.color}30`
    }}>
      {info.icon}
      {info.label}
    </span>
  );
};

// Progress Bar
const ProgressBar = ({ progress }) => {
  const getColor = (p) => {
    if (p >= 67) return TOKENS.success;
    if (p >= 34) return TOKENS.warning;
    return TOKENS.danger;
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.7rem',
        color: TOKENS.textSecondary,
        marginBottom: '6px',
        fontWeight: 500,
        letterSpacing: '0.01em'
      }}>
        <span style={{ color: TOKENS.textMuted }}>Progress</span>
        <span style={{ color: getColor(progress), fontWeight: 700 }}>{progress}%</span>
      </div>
      <div style={{
        width: '100%',
        height: '6px',
        backgroundColor: TOKENS.bgSurface,
        borderRadius: TOKENS.radiusFull,
        overflow: 'hidden',
        border: `1px solid ${TOKENS.borderSubtle}`
      }}>
        <div style={{
          width: `${progress}%`,
          height: '100%',
          backgroundColor: getColor(progress),
          borderRadius: TOKENS.radiusFull,
          transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: `0 0 8px ${getColor(progress)}40`
        }} />
      </div>
    </div>
  );
};

// PPN Card Component with File Attachment
const PPNCard = ({ ppn, onAddComment, comments = [], engine, currentUser, showNotification }) => {
  const [expanded, setExpanded] = useState(false);

  console.log('📎 PPNCard - File Attachment props:', {
    taskId: ppn.eventId,
    selectedWeek: ppn.selectedWeek,
    objectiveId: ppn.objectiveId,
    ppnName: ppn.taskName
  });

  return (
    <Card style={{
      padding: '0',
      boxShadow: TOKENS.shadowMd,
      border: `1px solid ${TOKENS.borderSubtle}`,
      transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
      backgroundColor: TOKENS.bgCard,
      marginBottom: '14px',
      position: 'relative',
      overflow: 'hidden',
      borderLeft: `3px solid ${ppn.progressPercent === 100 ? TOKENS.success : TOKENS.accent}`,
    }}>
      {/* Header */}
      <div style={{ 
        padding: '16px 18px 12px 18px',
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        gap: '16px'
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: TOKENS.radiusSm,
              backgroundColor: ppn.progressPercent === 100 ? `${TOKENS.success}14` : `${TOKENS.accent}14`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <FileIcon size={16} style={{ color: ppn.progressPercent === 100 ? TOKENS.success : TOKENS.accent }} />
            </div>
            <h4 style={{ 
              margin: 0, 
              fontSize: '0.95rem',
              color: TOKENS.textPrimary,
              fontWeight: 600,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {ppn.taskName}
            </h4>
          </div>
          {ppn.taskDescription && (
            <div style={{ 
              fontSize: '0.82rem', 
              color: TOKENS.textSecondary,
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: expanded ? 'none' : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              marginLeft: '42px',
            }}>
              {ppn.taskDescription}
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0 }}>
          <StatusBadge progress={ppn.progressPercent} />
        </div>
      </div>

      {/* Progress */}
      <div style={{ padding: '0 18px' }}>
        <ProgressBar progress={ppn.progressPercent} />
      </div>

      {/* Quick Info */}
      <div style={{
        display: 'flex',
        gap: '14px',
        fontSize: '0.75rem',
        color: TOKENS.textMuted,
        marginTop: '12px',
        padding: '0 18px',
        flexWrap: 'wrap',
        alignItems: 'center',
        fontWeight: 500
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
          <ClockIcon size={13} />
          {ppn.selectedWeek}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: TOKENS.textMuted, display: 'inline-block' }} />
          {new Date(ppn.lastUpdated).toLocaleDateString()}
        </span>
        {comments.length > 0 && (
          <span style={{ color: TOKENS.accentText, display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: 600 }}>
            <MessageCircleIcon size={13} />
            {comments.length} comment{comments.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Actions */}
      <div style={{
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        borderTop: `1px solid ${TOKENS.borderSubtle}`,
        padding: '12px 18px',
        marginTop: '14px',
        backgroundColor: TOKENS.bgSurface,
      }}>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            backgroundColor: TOKENS.bgCard,
            border: `1px solid ${TOKENS.borderSubtle}`,
            borderRadius: TOKENS.radiusFull,
            padding: '6px 14px',
            fontSize: '0.78rem',
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            color: TOKENS.textPrimary,
            transition: 'all 0.2s ease',
            boxShadow: TOKENS.shadowSm
          }}
        >
          {expanded ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
          {expanded ? 'Hide Details' : 'View Details'}
        </button>

        <button
          onClick={() => onAddComment(ppn)}
          style={{
            backgroundColor: TOKENS.accentSoft,
            border: `1px solid ${TOKENS.accent}30`,
            borderRadius: TOKENS.radiusFull,
            padding: '6px 14px',
            fontSize: '0.78rem',
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: TOKENS.accentText,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <MessageCircleIcon size={14} />
          Add Comment
        </button>
      </div>

      {/* File Attachment - Read Only for Managers */}
      <div style={{ marginTop: '12px', padding: '0 18px 16px 18px' }}>
        <FileAttachment
          taskId={ppn.eventId}
          selectedWeek={ppn.selectedWeek}
          objectiveId={ppn.objectiveId}
          engine={engine}
          isFormDisabled={false}   // ← allow uploads
          showNotification={showNotification}
          currentUser={currentUser}
          compact={true}
        />
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div style={{
          margin: '0 14px 14px 14px',
          padding: '14px',
          backgroundColor: TOKENS.bgCard,
          borderRadius: TOKENS.radiusSm,
          border: `1px solid ${TOKENS.borderSubtle}`,
        }}>
          {ppn.achievements && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '3px 10px',
                borderRadius: TOKENS.radiusFull,
                backgroundColor: `${TOKENS.success}14`,
                color: TOKENS.success,
                fontSize: '0.72rem',
                fontWeight: 600,
                marginBottom: '8px',
                border: `1px solid ${TOKENS.success}25`
              }}>
                🏆 Achievements
              </div>
              <div style={{ 
                padding: '10px 14px',
                backgroundColor: TOKENS.bgSurface,
                borderRadius: TOKENS.radiusXs,
                border: `1px solid ${TOKENS.borderSubtle}`,
                whiteSpace: 'pre-wrap',
                fontSize: '0.85rem',
                color: TOKENS.textPrimary,
                lineHeight: 1.6
              }}>
                {ppn.achievements}
              </div>
            </div>
          )}

          {ppn.challenges && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '3px 10px',
                borderRadius: TOKENS.radiusFull,
                backgroundColor: `${TOKENS.danger}14`,
                color: TOKENS.danger,
                fontSize: '0.72rem',
                fontWeight: 600,
                marginBottom: '8px',
                border: `1px solid ${TOKENS.danger}25`
              }}>
                ⚠️ Challenges
              </div>
              <div style={{ 
                padding: '10px 14px',
                backgroundColor: TOKENS.bgSurface,
                borderRadius: TOKENS.radiusXs,
                border: `1px solid ${TOKENS.borderSubtle}`,
                whiteSpace: 'pre-wrap',
                fontSize: '0.85rem',
                color: TOKENS.textPrimary,
                lineHeight: 1.6
              }}>
                {ppn.challenges}
              </div>
            </div>
          )}

          {ppn.nextSteps && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '3px 10px',
                borderRadius: TOKENS.radiusFull,
                backgroundColor: `${TOKENS.accent}14`,
                color: TOKENS.accentText,
                fontSize: '0.72rem',
                fontWeight: 600,
                marginBottom: '8px',
                border: `1px solid ${TOKENS.accent}25`
              }}>
                📋 Next Steps
              </div>
              <div style={{ 
                padding: '10px 14px',
                backgroundColor: TOKENS.bgSurface,
                borderRadius: TOKENS.radiusXs,
                border: `1px solid ${TOKENS.borderSubtle}`,
                whiteSpace: 'pre-wrap',
                fontSize: '0.85rem',
                color: TOKENS.textPrimary,
                lineHeight: 1.6
              }}>
                {ppn.nextSteps}
              </div>
            </div>
          )}

          {/* Comments */}
          {comments.length > 0 && (
            <div style={{ marginTop: '12px', borderTop: `1px solid ${TOKENS.borderSubtle}`, paddingTop: '12px' }}>
              <div style={{ fontWeight: 600, color: TOKENS.textPrimary, marginBottom: '10px', fontSize: '0.9rem' }}>
                💬 Comments ({comments.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {comments.map((comment, idx) => (
                  <div key={idx} style={{
                    padding: '10px 14px',
                    backgroundColor: TOKENS.bgSurface,
                    borderRadius: TOKENS.radiusSm,
                    borderLeft: `3px solid ${TOKENS.accent}`,
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{ fontSize: '0.72rem', color: TOKENS.textMuted, fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        backgroundColor: `${TOKENS.accent}18`,
                        color: TOKENS.accent,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 700
                      }}>
                        {(comment.commentedBy || 'U')[0]?.toUpperCase()}
                      </span>
                      <strong style={{ color: TOKENS.textSecondary }}>{comment.commentedBy}</strong>
                      <span>•</span>
                      <span>{new Date(comment.commentedAt).toLocaleString()}</span>
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '0.85rem', color: TOKENS.textPrimary, lineHeight: 1.5 }}>{comment.comment}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// ============================================================================
// Team Member Card - Modern pill-style buttons, glowing selection ring
// ============================================================================
const TeamMemberCard = ({ member, ppns = [], isSelected, onSelect }) => {
  const totalPPNs = ppns.length;
  const completedPPNs = ppns.filter(p => p.progressPercent === 100).length;
  const avgProgress = totalPPNs > 0 
    ? Math.round(ppns.reduce((sum, p) => sum + p.progressPercent, 0) / totalPPNs)
    : 0;

  return (
    <div
      onClick={() => onSelect(member)}
      style={{
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
        transform: isSelected ? 'scale(1.02)' : 'scale(1)',
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.transform = 'translateY(-5px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      <Card
        style={{
          padding: '16px',
          border: isSelected ? `2px solid ${TOKENS.accent}` : `1px solid ${TOKENS.borderSubtle}`,
          backgroundColor: isSelected ? TOKENS.accentSoft : TOKENS.bgCard,
          transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: isSelected 
            ? `0 0 0 4px ${TOKENS.accent}18, ${TOKENS.shadowLg}` 
            : TOKENS.shadowMd,
          marginBottom: '0',
          position: 'relative',
          height: '100%',
          borderRadius: TOKENS.radiusLg,
        }}
      >
        {isSelected && (
          <div style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            backgroundColor: TOKENS.accent,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${TOKENS.accent}40`,
            zIndex: 2
          }}>
            <CheckCircleIcon size={16} />
          </div>
        )}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '14px',
          padding: '4px'
        }}>
          <div style={{
            width: '46px',
            height: '46px',
            borderRadius: TOKENS.radiusMd,
            backgroundColor: isSelected ? TOKENS.accent : `${TOKENS.textMuted}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isSelected ? 'white' : TOKENS.textMuted,
            flexShrink: 0,
            transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
            boxShadow: isSelected ? `0 4px 12px ${TOKENS.accent}30` : TOKENS.shadowSm,
            fontSize: '1.25rem',
            fontWeight: 700,
            letterSpacing: '0.02em'
          }}>
            {member.displayName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: 600, 
              fontSize: '0.95rem',
              color: isSelected ? TOKENS.accentText : TOKENS.textPrimary,
              marginBottom: '2px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {member.displayName}
            </div>
            <div style={{ fontSize: '0.78rem', color: TOKENS.textSecondary, marginBottom: '8px' }}>
              {member.positionName || 'Position not set'}
            </div>
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              flexWrap: 'wrap'
            }}>
              <span style={{
                padding: '2px 10px',
                borderRadius: TOKENS.radiusFull,
                backgroundColor: `${TOKENS.accent}10`,
                color: TOKENS.accentText,
                fontSize: '0.7rem',
                fontWeight: 600,
                border: `1px solid ${TOKENS.accent}20`
              }}>
                <GiNotebook /> {totalPPNs} PPNs
              </span>
              <span style={{
                padding: '2px 10px',
                borderRadius: TOKENS.radiusFull,
                backgroundColor: `${TOKENS.success}10`,
                color: TOKENS.success,
                fontSize: '0.7rem',
                fontWeight: 600,
                border: `1px solid ${TOKENS.success}20`
              }}>
                <CheckCircleIcon size={12} /> {completedPPNs} Complete
              </span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ManagerPPNView = () => {
  const engine = useDataEngine();
  const { user } = useAuth();
  const { currentUserPosition: contextPosition } = usePerformanceContext();
  const navigate = useNavigate();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserPosition, setCurrentUserPosition] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberPPNs, setMemberPPNs] = useState({});
  const [selectedWeek, setSelectedWeek] = useState('');
  const [weeks, setWeeks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [commentModal, setCommentModal] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState({});
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const [fiscalYear, setFiscalYear] = useState({ start: '', end: '' });
  const [fiscalYearLoaded, setFiscalYearLoaded] = useState(false);

  useEffect(() => {
    const fy = getFiscalYear();
    setFiscalYear(fy);
    setFiscalYearLoaded(true);
  }, []);

  // Notification function (keep existing)
  const showNotification = (message, type = 'info') => {
    console.log(`[${type}] ${message}`);
  };
  

  // Fetch user position directly if not available from context
  const fetchUserPosition = useCallback(async () => {
    if (!user?.id) return null;

    try {
      console.log('🔍 [ManagerPPNView] Fetching position for user:', user.id);
      
      const response = await engine.query({
        currentUserPosition: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E',
            filter: `V66IFsw5kq6:eq:${user.id}`,
            fields: 'trackedEntityInstance,attributes[attribute,value]',
            ouMode: 'ACCESSIBLE',
            pageSize: 1
          }
        }
      });

      const position = response.currentUserPosition?.trackedEntityInstances?.[0];
      if (position) {
        console.log('✅ [ManagerPPNView] Found position:', position.trackedEntityInstance);
        return position;
      } else {
        console.warn('⚠️ [ManagerPPNView] No position found for user:', user.id);
        return null;
      }
    } catch (error) {
      console.error('❌ [ManagerPPNView] Error fetching position:', error);
      return null;
    }
  }, [user?.id, engine]);

  // Fetch team members
  const fetchTeamMembers = useCallback(async (position) => {
    const positionId = position?.trackedEntityInstance || contextPosition?.trackedEntityInstance;
    
    if (!positionId) {
      console.log('⚠️ [ManagerPPNView] No position available to fetch team members');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      console.log('🔍 [ManagerPPNView] Fetching subordinates for position:', positionId);
      
      const response = await engine.query({
        subordinatePositions: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E',
            filter: `SjP47qi3JeB:eq:${positionId}`,
            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
            ouMode: 'ACCESSIBLE',
            pageSize: 100
          }
        }
      });

      const positions = response.subordinatePositions?.trackedEntityInstances || [];
      console.log('📊 [ManagerPPNView] Found positions:', positions.length);
      
      const members = [];

      for (const pos of positions) {
        const attrs = {};
        pos.attributes?.forEach(attr => {
          attrs[attr.attribute] = attr.value;
        });

        const positionTitle = attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 
                            attrs['x4c3SqL2mN'] || 
                            'Position not set';

        console.log(`📋 Position ${pos.trackedEntityInstance} title:`, positionTitle);

        const incumbentId = attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
        if (incumbentId) {
          try {
            const userResponse = await engine.query({
              user: {
                resource: `users/${incumbentId}`,
                params: { fields: 'id,displayName,email,userCredentials[username]' }
              }
            });
            
            if (userResponse.user) {
              members.push({
                id: userResponse.user.id,
                displayName: userResponse.user.displayName || 
                           userResponse.user.userCredentials?.username || 
                           incumbentId,
                positionId: pos.trackedEntityInstance,
                positionName: positionTitle,
                email: userResponse.user.email
              });
            }
          } catch (userError) {
            console.warn('Failed to fetch user details for incumbent:', incumbentId);
          }
        }
      }

      setTeamMembers(members);
      
      if (members.length > 0) {
        setSelectedMember(members[0]);
      }
      
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoading(false);
    }
  }, [engine, contextPosition]);

  // Fetch PPNs for all team members
  const fetchAllMemberPPNs = useCallback(async () => {

    if (!fiscalYear) {
      showNotification('Fiscal year not configured. Please contact your administrator.', 'warning');
      setIsLoading(false);
      return;
    }

    if (teamMembers.length === 0) return;

    try {
      const allPPNs = {};
      const allWeeksSet = new Set();

      for (const member of teamMembers) {
        const response = await engine.query({
          ppns: {
            resource: 'events',
            params: {
              program: PROGRAM_ID,
              programStage: TASK_PROGRAM_STAGE_ID,
              filter: [`SlgO60cMK3F:eq:${member.positionId}`],
              fields: 'event,eventDate,orgUnitName,dataValues[dataElement,value],lastUpdated',
              pageSize: 1000,
              order: 'eventDate:desc'
            }
          }
        });

        const ppns = response.ppns?.events?.map(event => {
          const dataValues = event.dataValues || [];
          const week = dataValues.find(dv => dv.dataElement === 'EiL3lPaL908')?.value || '';
          
          return {
            eventId: event.event,
            selectedWeek: week,
            progressPercent: parseInt(dataValues.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value) || 0,
            status: dataValues.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value || '',
            achievements: dataValues.find(dv => dv.dataElement === 'j9FdNkmPpXM')?.value || '',
            challenges: dataValues.find(dv => dv.dataElement === 'WSQ9nuXPnNL')?.value || '',
            nextSteps: dataValues.find(dv => dv.dataElement === 'M7PG2p2XsDA')?.value || '',
            taskName: dataValues.find(dv => dv.dataElement === 'I0QtZkMbzxN')?.value || 'Unnamed Task',
            taskDescription: dataValues.find(dv => dv.dataElement === 'LR70eqTjB8m')?.value || '',
            kpiId: dataValues.find(dv => dv.dataElement === 'uXeH0X0aaCl')?.value || '',
            objectiveId: dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value || '',
            lastUpdated: event.lastUpdated
          };
        }) || [];

        // ✅ Filter PPNs by fiscal year based on selectedWeek
        const filteredPPNs = ppns.filter(ppn => 
          ppn.selectedWeek && isWithinFiscalYear(ppn.selectedWeek, fiscalYear)
        );

        allPPNs[member.positionId] = filteredPPNs;
        filteredPPNs.forEach(p => {
          if (p.selectedWeek) allWeeksSet.add(p.selectedWeek);
        });
      }

      setMemberPPNs(allPPNs);
      
      const uniqueWeeks = [...allWeeksSet].sort((a, b) => new Date(b) - new Date(a));
      setWeeks(uniqueWeeks);
      
      if (uniqueWeeks.length > 0 && !selectedWeek) {
        setSelectedWeek(uniqueWeeks[0]);
      }

    } catch (error) {
      console.error('Error fetching PPNs:', error);
    }
  }, [teamMembers, engine, fiscalYear, selectedWeek]);
  

  // Load comments
  const loadComments = (ppnId) => {
    if (!ppnId) return [];
    try {
      const storageKey = `ppn_comments_${ppnId}`;
      return JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch {
      return [];
    }
  };

  // Handle comment submission
  const handleSubmitComment = async () => {
    if (!commentText.trim() || !commentModal) return;
    
    setIsSubmittingComment(true);
    try {
      const commentData = {
        ppnId: commentModal.eventId,
        comment: commentText.trim(),
        commentedBy: user?.displayName || user?.username || 'Unknown',
        commentedAt: new Date().toISOString()
      };
      
      const storageKey = `ppn_comments_${commentModal.eventId}`;
      const existingComments = JSON.parse(localStorage.getItem(storageKey) || '[]');
      existingComments.push(commentData);
      localStorage.setItem(storageKey, JSON.stringify(existingComments));
      
      setComments(prev => ({
        ...prev,
        [commentModal.eventId]: existingComments
      }));
      
      setCommentModal(null);
      setCommentText('');
    } catch (error) {
      console.error('Error saving comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Get PPNs for selected member with filters
  const getFilteredPPNs = useCallback(() => {
    if (!selectedMember) return [];
    
    let ppns = memberPPNs[selectedMember.positionId] || [];
    
    if (selectedWeek) {
      ppns = ppns.filter(p => p.selectedWeek === selectedWeek);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      ppns = ppns.filter(p => 
        p.taskName.toLowerCase().includes(term) ||
        (p.taskDescription?.toLowerCase() || '').includes(term)
      );
    }
    
    if (filterStatus !== 'all') {
      if (filterStatus === 'completed') {
        ppns = ppns.filter(p => p.progressPercent === 100);
      } else if (filterStatus === 'in-progress') {
        // ✅ Any progress less than 100% is "In Progress"
        ppns = ppns.filter(p => p.progressPercent < 100);
      }
    }
    
    return ppns;
  }, [selectedMember, memberPPNs, selectedWeek, searchTerm, filterStatus]);

  // Group PPNs by week
  const groupPPNsByWeek = (ppns) => {
    const grouped = {};
    ppns.forEach(ppn => {
      if (!grouped[ppn.selectedWeek]) {
        grouped[ppn.selectedWeek] = [];
      }
      grouped[ppn.selectedWeek].push(ppn);
    });
    return grouped;
  };

  // Initialize
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      
      let position = contextPosition;
      
      if (!position?.trackedEntityInstance) {
        position = await fetchUserPosition();
        if (position) {
          setCurrentUserPosition(position);
        }
      } else {
        setCurrentUserPosition(contextPosition);
      }
      
      if (position?.trackedEntityInstance) {
        await fetchTeamMembers(position);
      } else {
        console.warn('⚠️ [ManagerPPNView] No position available after initialization');
        setIsLoading(false);
      }
    };
    
    initialize();
  }, [contextPosition, fetchUserPosition, fetchTeamMembers]);

  // Fetch PPNs when team members change
  useEffect(() => {
    if (teamMembers.length > 0) {
      fetchAllMemberPPNs();
    }
  }, [teamMembers, fetchAllMemberPPNs]);

  // Loading state
  if (isLoading || !fiscalYearLoaded) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: TOKENS.bgPrimary
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          border: `3px solid ${TOKENS.borderSubtle}`,
          borderTopColor: TOKENS.accent,
          animation: 'spin 1s linear infinite'
        }} />
        <span style={{ color: TOKENS.textSecondary, fontWeight: 500 }}>Loading team data...</span>
      </div>
    );
  }

  // No team members
  if (teamMembers.length === 0) {
    return (
      <div style={{ 
        padding: '60px 40px', 
        textAlign: 'center',
        backgroundColor: TOKENS.bgPrimary 
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: `${TOKENS.textMuted}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px auto',
          fontSize: '2.5rem'
        }}>
          👥
        </div>
        <h3 style={{ color: TOKENS.textPrimary, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 8px 0' }}>
          No Team Members Found
        </h3>
        <p style={{ color: TOKENS.textSecondary, maxWidth: '400px', margin: '0 auto 24px auto', lineHeight: 1.6 }}>
          You don't have any direct reports assigned to you.
        </p>
        <button
          onClick={() => navigate('/ppn')}
          style={{
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            padding: '10px 24px',
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: `0 4px 14px ${TOKENS.accent}40`,
            transition: 'all 0.2s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '4px',
          }}
        >
          <ChevronLeft size={16} />
          Back to My PPN
        </button>
      </div>
    );
  }

  const filteredPPNs = getFilteredPPNs();
  const groupedPPNs = groupPPNsByWeek(filteredPPNs);

  return (
    <div style={{ 
      padding: '28px', 
      maxWidth: '1400px', 
      margin: '0 auto',
      backgroundColor: TOKENS.bgPrimary,
      minHeight: '100vh'
    }}>
      {/* Navigation */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => navigate('/ppn')}
          style={{
            backgroundColor: '#1976d2',
            color: 'white',
            border: `1px solid ${TOKENS.borderSubtle}`,
            borderRadius: '4px',
            padding: '8px 18px',
            fontSize: '0.85rem',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
            boxShadow: TOKENS.shadowSm
          }}
        >
          <ChevronLeft size={16} />
          Back to My PPN
        </button>
      </div>

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '28px',
        flexWrap: 'wrap',
        gap: '18px',
        backgroundColor: TOKENS.bgCard,
        padding: '20px 24px',
        borderRadius: TOKENS.radiusLg,
        boxShadow: TOKENS.shadowMd,
        border: `1px solid ${TOKENS.borderSubtle}`
      }}>
        <div>
          <h3 style={{ 
            margin: 0, 
            color: TOKENS.textPrimary, 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            fontSize: '1.25rem',
            fontWeight: 700,
            letterSpacing: '-0.01em'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: TOKENS.radiusSm,
              backgroundColor: `${TOKENS.accent}14`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: TOKENS.accent
            }}>
              <UsersRoundIcon size={20} />
            </div>
            Team Performance Notes
          </h3>
          <p style={{ margin: '6px 0 0 42px', color: TOKENS.textSecondary, fontSize: '0.9rem', lineHeight: 1.5 }}>
            Click on any team member card below to view their PPNs
          </p>
        </div>
        {selectedMember && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: TOKENS.accentSoft,
            borderRadius: TOKENS.radiusFull,
            fontSize: '0.85rem',
            color: TOKENS.accentText,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            border: `1px solid ${TOKENS.accent}20`
          }}>
            <div style={{
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              backgroundColor: TOKENS.accent,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 700
            }}>
              {selectedMember.displayName[0]?.toUpperCase()}
            </div>
            Viewing: {selectedMember.displayName}
          </div>
        )}
      </div>

      {/* Team Members Grid */}
      <div style={{ marginBottom: '28px' }}>
        <h4 style={{ 
          margin: '0 0 14px 0', 
          color: TOKENS.textPrimary, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px',
          fontSize: '1rem',
          fontWeight: 700,
          paddingLeft: '4px'
        }}>
          <div style={{
            width: '28px',
            height: '28px',
            borderRadius: TOKENS.radiusXs,
            backgroundColor: `${TOKENS.accent}14`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: TOKENS.accent
          }}>
            <Users size={16} />
          </div>
          Team Overview
        </h4>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px'
        }}>
          {teamMembers.map(member => (
            <TeamMemberCard
              key={member.id}
              member={member}
              ppns={memberPPNs[member.positionId] || []}
              isSelected={selectedMember?.id === member.id}
              onSelect={setSelectedMember}
            />
          ))}
        </div>
      </div>

      {/* PPN View Area */}
      {selectedMember && (
        <div>
          {/* Filters */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '22px',
            alignItems: 'center',
            backgroundColor: TOKENS.bgCard,
            padding: '14px 18px',
            borderRadius: TOKENS.radiusLg,
            boxShadow: TOKENS.shadowMd,
            border: `1px solid ${TOKENS.borderSubtle}`
          }}>
            <div style={{ flex: 1, minWidth: '220px' }}>
              <Input
                placeholder="Search PPNs..."
                value={searchTerm}
                onChange={({ value }) => setSearchTerm(value)}
                style={{ 
                  width: '100%',
                  borderRadius: TOKENS.radiusSm,
                  borderColor: TOKENS.borderSubtle,
                  backgroundColor: TOKENS.bgCard
                }}
                prefix={<IconSearch24 color={TOKENS.textMuted} />}
              />
            </div>
            
            <div style={{ minWidth: '160px' }}>
              <SingleSelect
                selected={filterStatus}
                onChange={({ selected }) => setFilterStatus(selected)}
                style={{ 
                  width: '100%',
                  borderRadius: TOKENS.radiusSm,
                  borderColor: TOKENS.borderSubtle,
                  backgroundColor: TOKENS.bgCard
                }}
              >
                <SingleSelectOption value="all" label="All Status" />
                <SingleSelectOption value="completed" label="✅ Complete" />
                <SingleSelectOption value="in-progress" label="🔄 In Progress" />
              </SingleSelect>
            </div>

            {weeks.length > 0 && (
              <div style={{ minWidth: '160px' }}>
                <SingleSelect
                  selected={selectedWeek}
                  onChange={({ selected }) => setSelectedWeek(selected)}
                  style={{ 
                    width: '100%',
                    borderRadius: TOKENS.radiusSm,
                    borderColor: TOKENS.borderSubtle,
                    backgroundColor: TOKENS.bgCard
                  }}
                >
                  {weeks.map(week => (
                    <SingleSelectOption key={week} value={week} label={`📅 ${week}`} />
                  ))}
                </SingleSelect>
              </div>
            )}
          </div>

          {/* PPN Cards Grouped by Week */}
          {filteredPPNs.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '70px 20px',
              backgroundColor: TOKENS.bgCard,
              borderRadius: TOKENS.radiusLg,
              color: TOKENS.textMuted,
              border: `1px solid ${TOKENS.borderSubtle}`,
              boxShadow: TOKENS.shadowMd
            }}>
              <div style={{ 
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                backgroundColor: TOKENS.bgSurface,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 18px auto',
                fontSize: '2rem',
                border: `1px solid ${TOKENS.borderSubtle}`
              }}>
                <GiNotebook />
              </div>
              <p style={{ fontSize: '1.15rem', fontWeight: 600, color: TOKENS.textPrimary, margin: '0 0 6px 0' }}>No PPNs found</p>
              <p style={{ fontSize: '0.9rem', color: TOKENS.textSecondary, maxWidth: '380px', margin: '0 auto', lineHeight: 1.6 }}>
                {searchTerm ? 'Try adjusting your search or filters' : 'This team member has no PPNs for the selected week'}
              </p>
            </div>
          ) : (
            <div>
              {Object.entries(groupedPPNs).map(([week, ppns]) => (
                <div key={week} style={{ marginBottom: '28px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '14px',
                    padding: '10px 16px',
                    backgroundColor: TOKENS.bgCard,
                    borderRadius: TOKENS.radiusSm,
                    boxShadow: TOKENS.shadowSm,
                    border: `1px solid ${TOKENS.borderSubtle}`
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: TOKENS.radiusXs,
                      backgroundColor: `${TOKENS.accent}14`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: TOKENS.accent,
                      flexShrink: 0
                    }}>
                      <ClockIcon size={16} />
                    </div>
                    <strong style={{ 
                      color: TOKENS.textPrimary,
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      letterSpacing: '0.01em'
                    }}>
                      Week Ending: {week}
                    </strong>
                    <div style={{
                      padding: '3px 12px',
                      backgroundColor: TOKENS.bgSurface,
                      borderRadius: TOKENS.radiusFull,
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: TOKENS.textSecondary,
                      border: `1px solid ${TOKENS.borderSubtle}`,
                      marginLeft: 'auto'
                    }}>
                      {ppns.length} PPN{ppns.length !== 1 ? 's' : ''}
                    </div>
                  </div>

                  <div>
                    {ppns.map(ppn => {
                      const ppnComments = loadComments(ppn.eventId);
                      return (
                        <PPNCard
                          key={ppn.eventId}
                          ppn={ppn}
                          comments={ppnComments}
                          onAddComment={(p) => {
                            setCommentModal(p);
                            setCommentText('');
                          }}
                          engine={engine}
                          currentUser={user}
                          showNotification={showNotification}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Comment Modal */}
      {commentModal && (
        <Modal large onClose={() => setCommentModal(null)}>
          <ModalTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: TOKENS.radiusSm,
                backgroundColor: TOKENS.accentSoft,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: TOKENS.accent
              }}>
                <MessageCircleIcon size={18} />
              </div>
              <span style={{ fontWeight: 600, color: TOKENS.textPrimary }}>Add Comment for: {commentModal.taskName}</span>
            </div>
          </ModalTitle>
          <ModalContent>
            <div style={{ padding: '20px' }}>
              <div style={{
                padding: '14px',
                backgroundColor: TOKENS.bgSurface,
                borderRadius: TOKENS.radiusSm,
                marginBottom: '20px',
                border: `1px solid ${TOKENS.borderSubtle}`,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px'
              }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: TOKENS.textMuted, fontWeight: 500, marginBottom: '2px' }}>Team Member</div>
                  <div style={{ fontSize: '0.85rem', color: TOKENS.textPrimary, fontWeight: 600 }}>{selectedMember?.displayName}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: TOKENS.textMuted, fontWeight: 500, marginBottom: '2px' }}>Week</div>
                  <div style={{ fontSize: '0.85rem', color: TOKENS.textPrimary, fontWeight: 600 }}>{commentModal.selectedWeek}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: TOKENS.textMuted, fontWeight: 500, marginBottom: '2px' }}>Progress</div>
                  <div style={{ fontSize: '0.85rem', color: TOKENS.textPrimary, fontWeight: 600 }}>{commentModal.progressPercent}%</div>
                </div>
              </div>

              <TextArea
                value={commentText}
                onChange={({ value }) => setCommentText(value)}
                placeholder="Enter your feedback or comment here..."
                rows={4}
                style={{ 
                  width: '100%',
                  borderRadius: TOKENS.radiusSm,
                  borderColor: TOKENS.borderSubtle,
                  backgroundColor: TOKENS.bgCard
                }}
              />
            </div>
          </ModalContent>
          <ModalActions>
            <button
              onClick={() => setCommentModal(null)}
              style={{
                backgroundColor: TOKENS.bgCard,
                color: TOKENS.textSecondary,
                border: `1px solid ${TOKENS.borderSubtle}`,
                borderRadius: TOKENS.radiusSm,
                padding: '8px 18px',
                fontSize: '0.85rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Cancel
            </button>
            <button 
              style={{
                backgroundColor: TOKENS.accent,
                color: 'white',
                border: 'none',
                borderRadius: TOKENS.radiusSm,
                padding: '8px 18px',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: `0 4px 14px ${TOKENS.accent}40`,
                transition: 'all 0.2s ease',
                opacity: (!commentText.trim() || isSubmittingComment) ? 0.5 : 1
              }}
              onClick={handleSubmitComment}
              disabled={!commentText.trim() || isSubmittingComment}
            >
              {isSubmittingComment ? 'Saving...' : 'Add Comment'}
            </button>
          </ModalActions>
        </Modal>
      )}
    </div>
  );
};

export default ManagerPPNView;